import { type FC, type ReactNode, useEffect, useState, useRef, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import CredentialingContext from './CredentialingContext';
import { CREDENTIALING_QUERY } from './queries';
import { type LiabilityInsurance as ILiabilityInsurance, type License } from '../../../interfaces';
import { useSearchFiles } from 'api/queries/fileUploadQuery';

interface CredentialingProviderProps {
  providerId: number;
  children: ReactNode;
}

export const CredentialingProvider: FC<CredentialingProviderProps> = ({ providerId, children }) => {
  const { data: allAttachments } = useSearchFiles({ Type: 'provider', Id: providerId });
  const liabilityInsuranceHandleRenew = useRef<(() => void) | null>(null);
  const queryClient = useQueryClient();

  const { data, loading: credentialsLoading } = useQuery(CREDENTIALING_QUERY, {
    fetchPolicy: 'network-only',
    variables: { providerId },
    errorPolicy: 'all'
  });

  
  const attachFilesToLicense = (license?: License) => {
    return { 
      ...license,
      attachments: allAttachments?.filter(attachment => (
        attachment?.metadata.providerLicenseId === license?.providerLicenseId.toString()
        && attachment?.metadata.document_type === 'professional-membership-license'
      ))
    } as License;
  }


  const [isInsuranceDocumentExpiring, setIsInsuranceDocumentExpiring] = useState(false);
  const [isInsuranceDocumentExpired, setIsInsuranceDocumentExpired] = useState(false);
  const [liabilityInsurance, setLiabilityInsurance] = useState<ILiabilityInsurance | undefined>(undefined);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [expiredLicenses, setExpiredLicenses] = useState<number[]>([]);
  const [expiringLicenses, setExpiringLicenses] = useState<number[]>([]);


  useEffect(() => {
    if (data?.provider?.liabilityInsurance) {
      const insurance = data.provider.liabilityInsurance;
      const insuranceWithAttachments = {
        ...insurance,
        attachments: allAttachments?.filter(attachment => (
          attachment?.metadata.providerId === providerId.toString()
          && attachment?.metadata?.document_type === 'liability-insurance-certificate'
        ))
      } as ILiabilityInsurance;

      const expiryDate = insuranceWithAttachments.expiryDate ? moment(insuranceWithAttachments.expiryDate) : null;

      setIsInsuranceDocumentExpiring(expiryDate?.endOf('day')?.isBetween(moment().startOf('day'), moment().add(30, 'days')) ?? false);
      setIsInsuranceDocumentExpired(expiryDate?.endOf('day')?.isBefore(moment()) ?? false);
      setLiabilityInsurance(insuranceWithAttachments);
    }
    if (data?.provider?.credential?.licenses?.length) {
      const licensesWithAttachments = data.provider.credential.licenses.map(attachFilesToLicense);
      const expired = licensesWithAttachments.filter(lic => moment().isAfter(moment(lic.expiryDate).endOf('day'))).map(lic => lic.providerLicenseId);
      const expiring = licensesWithAttachments.filter(lic => moment(lic.expiryDate).endOf('day').isBetween(moment().startOf('day'), moment().add(30, 'days'))).map(lic => lic.providerLicenseId);
      setLicenses(licensesWithAttachments);
      setExpiredLicenses(expired);
      setExpiringLicenses(expiring.filter(licenseId => !expired.includes(licenseId)));
    }
  }, [data?.provider?.liabilityInsurance, allAttachments, providerId]);

  const setLiabilityInsuranceHandleRenew = (handleRenew: (() => void) | null) => {
    liabilityInsuranceHandleRenew.current = handleRenew;
  };

  const handleLiabilityInsuranceRenew = () => {
    if (liabilityInsuranceHandleRenew.current) {
      liabilityInsuranceHandleRenew.current();
    }
  };

  const refreshCredentialing = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['files', 'search', { Id: providerId, Type: 'provider' }]});
  }, [queryClient, providerId]);

  const contextValue = {
    isInsuranceDocumentExpiring,
    isInsuranceDocumentExpired,
    setLiabilityInsuranceHandleRenew,
    handleLiabilityInsuranceRenew,
    liabilityInsurance,
    refreshCredentialing,
    credentialsLoading,
    licenses,
    expiringLicenses,
    expiredLicenses,
  };

  return (
    <CredentialingContext.Provider value={contextValue}>
      {children}
    </CredentialingContext.Provider>
  );
};

export default CredentialingProvider;
